import ExcelJS from 'exceljs';
import { InvoiceItem } from '../config/types';
import { RootState } from '../store';
import { store } from '../store';

interface Mapping {
  cell: string;
  staticText: string | null;
  fieldName: string | null;
}

interface CellStyle {
  style?: any;
  font?: any;
  border?: any;
  fill?: any;
  numFmt?: string;
  alignment?: any;
}

// ExcelJS cannot serialize certain conditional-formatting rule types (notably
// `duplicateValues` and `uniqueValues`). When the template contains them, ExcelJS
// reads them into its model but the writer emits an empty <conditionalFormatting>
// element with no <cfRule> child. That is invalid OOXML, so Excel reports the
// sheet as unreadable and shows a "repaired content" prompt on open.
//
// We can't preserve those rules (ExcelJS drops them regardless), so strip the
// unsupported rule types and remove any conditional-formatting block that is left
// empty, before writing the workbook. This keeps the output schema-valid.
const UNSUPPORTED_CF_RULE_TYPES = new Set(['duplicateValues', 'uniqueValues']);

const sanitizeConditionalFormatting = (worksheet: ExcelJS.Worksheet): void => {
  const cfs = (worksheet as any).conditionalFormattings as
    | Array<{ ref: string; rules: Array<{ type: string }> }>
    | undefined;
  if (!Array.isArray(cfs) || cfs.length === 0) return;

  const cleaned = cfs
    .map((cf) => ({
      ...cf,
      rules: (cf.rules || []).filter((rule) => !UNSUPPORTED_CF_RULE_TYPES.has(rule.type)),
    }))
    .filter((cf) => cf.rules.length > 0);

  (worksheet as any).conditionalFormattings = cleaned;
};

const validateEntry = (data: InvoiceItem): boolean => {
  const requiredFields = [
    'destination', 'date', 'bolNumber', 'invoiceNumber', 
    'broker', 'loadNumber', 'terminal', 'gross', 'net', 
    'rate', 'fuelSurchargePercent', 'terminalArrivalTime', 
    'terminalDepartTime', 'deliveryArrivalTime', 
    'deliveryDepartTime', 'driver'
  ] as const;

  const missingFields = requiredFields.filter(field => !data[field]);
  if (missingFields.length > 0) {
    throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
  }

  // Validate numeric fields
  const numericFields = ['gross', 'net', 'rate', 'fuelSurchargePercent'] as const;
  const invalidNumericFields = numericFields.filter(field => 
    isNaN(parseFloat(data[field])) || parseFloat(data[field]) < 0
  );
  if (invalidNumericFields.length > 0) {
    throw new Error(`Invalid numeric values in fields: ${invalidNumericFields.join(', ')}`);
  }

  return true;
};

const calculateTimeDifference = (startTime: string | null, endTime: string | null): number => {
  if (!startTime || !endTime) return 0;

  // Convert times to 24-hour format if they're not already
  const convertTo24Hour = (timeStr: string): string | null => {
    if (!timeStr) return null;
    
    // If already in 24-hour format (HH:MM), return as is
    if (timeStr.match(/^\d{2}:\d{2}$/)) {
      return timeStr;
    }
    
    // Parse time with AM/PM
    const [time, modifier] = timeStr.split(' ');
    let [hours, minutes] = time.split(':');
    
    let hoursNum = parseInt(hours, 10);
    let minutesNum = parseInt(minutes, 10);
    
    if (modifier && modifier.toLowerCase() === 'pm' && hoursNum < 12) {
      hoursNum += 12;
    }
    if (modifier && modifier.toLowerCase() === 'am' && hoursNum === 12) {
      hoursNum = 0;
    }
    
    return `${hoursNum.toString().padStart(2, '0')}:${minutesNum.toString().padStart(2, '0')}`;
  };

  const start24 = convertTo24Hour(startTime);
  const end24 = convertTo24Hour(endTime);
  
  if (!start24 || !end24) return 0;

  const [startHours, startMinutes] = start24.split(':').map(Number);
  const [endHours, endMinutes] = end24.split(':').map(Number);
  
  const startTotalMinutes = startHours * 60 + startMinutes;
  const endTotalMinutes = endHours * 60 + endMinutes;
  
  // Calculate difference in minutes
  let diffMinutes = endTotalMinutes - startTotalMinutes;
  
  // If end time is earlier than start time, assume it's the next day
  if (diffMinutes < 0) {
    diffMinutes += 24 * 60;
  }
  
  // Convert to hours with decimal
  return parseFloat((diffMinutes / 60).toFixed(2));
};

const copyStyles = (sourceCell: CellStyle, targetCell: CellStyle): void => {
  if (sourceCell.style) {
    targetCell.style = JSON.parse(JSON.stringify(sourceCell.style));
  }
  if (sourceCell.font) {
    targetCell.font = JSON.parse(JSON.stringify(sourceCell.font));
  }
  if (sourceCell.border) {
    targetCell.border = JSON.parse(JSON.stringify(sourceCell.border));
  }
  if (sourceCell.fill) {
    targetCell.fill = JSON.parse(JSON.stringify(sourceCell.fill));
  }
  if (sourceCell.numFmt) {
    targetCell.numFmt = sourceCell.numFmt;
  }
  if (sourceCell.alignment) {
    targetCell.alignment = JSON.parse(JSON.stringify(sourceCell.alignment));
  }
};

const readMappingFile = async (filename: string): Promise<Mapping[]> => {
  try {
    console.log(`Reading mapping file: ${filename}`);
    const response = await fetch(`/templates/${filename}`);
    if (!response.ok) {
      throw new Error(`Failed to load mapping file: ${response.statusText}`);
    }
    const text = await response.text();
    console.log('Mapping file content:', text);

    const mappings: Mapping[] = [];
    
    // Parse each line - for regular Excel templates (Loads.txt)
    text.split('\n').forEach((line) => {
      line = line.trim();
      if (!line || line === '') return; // Skip empty lines
      
      // First character is the column letter(s)
      const columnMatch = line.match(/^([A-Z]+)/);
      if (!columnMatch) return;

      const column = columnMatch[1];
      // Rest of the line is the field name
      const fieldName = line.substring(column.length).trim();
      
      if (column && fieldName) {
        // All fields go to row 4 (first data row in template)
        mappings.push({
          cell: `${column}4`,
          staticText: null,
          fieldName: fieldName
        });
        console.log(`Parsed mapping: Column ${column}, Row 4, Field ${fieldName}, Cell ${column}4`);
      }
    });

    console.log('Final mappings:', mappings);
    return mappings;
  } catch (error) {
    console.error('Error reading mapping file:', error);
    throw error;
  }
};

const readInvoiceMappingFile = async (filename: string): Promise<Mapping[]> => {
  try {
    console.log(`Reading invoice mapping file: ${filename}`);
    const response = await fetch(`/templates/${filename}`);
    if (!response.ok) {
      throw new Error(`Failed to load mapping file: ${response.statusText}`);
    }
    const text = await response.text();
    console.log(`Invoice mapping file contents:`, text);

    const mappings: Mapping[] = [];
    const lines = text.split('\n');

    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine || trimmedLine.startsWith('#')) continue;

      const parts = trimmedLine.split(' ');
      if (parts.length < 2) continue;

      const cell = parts[0] + parts[1]; // Combine column and row
      const remainingParts = parts.slice(2).join(' ').trim();
      const isStaticText = remainingParts.startsWith('"') && remainingParts.endsWith('"');

      const mapping: Mapping = {
        cell,
        staticText: isStaticText ? remainingParts.slice(1, -1) : null,
        fieldName: isStaticText ? null : remainingParts
      };

      console.log(`Parsed invoice mapping:`, mapping);
      mappings.push(mapping);
    }

    console.log(`Total invoice mappings found: ${mappings.length}`);
    return mappings;
  } catch (error) {
    console.error(`Error reading invoice mapping file ${filename}:`, error);
    throw error;
  }
};

const getFieldValue = (entry: InvoiceItem, fieldName: string | null): string | number | null => {
  if (!fieldName) return null;

  const freightRev = parseFloat((parseFloat(entry.gross || '0') * parseFloat(entry.rate || '0')).toFixed(2));
  const fuelSurcharge = parseFloat(((parseFloat(freightRev.toString() || '0') * (parseFloat(entry.fuelSurchargePercent || '0') / 10)) / 10).toFixed(2));

  // Calculate time differences for demurrage
  const terminalTime = calculateTimeDifference(entry.terminalArrivalTime, entry.terminalDepartTime);
  const deliveryTime = calculateTimeDifference(entry.deliveryArrivalTime, entry.deliveryDepartTime);

  // Direct field mappings
  switch (fieldName) {
    case 'Customer Destination':
      return entry.destination;
    case 'Date':
      return entry.date;
    case 'BOL number':
      return entry.bolNumber;
    case 'Invoice number':
      return entry.invoiceNumber;
    case 'Order Number':
      return entry.orderNumber || '';
    case 'Load number':
      return entry.loadNumber;
    case 'Terminal':
      return entry.terminal;
    case 'Broker':
      return entry.broker;
    case 'Gross':
      return parseFloat(entry.gross);
    case 'Net':
      return parseFloat(entry.net);
    case 'Rate':
      return parseFloat(entry.rate);
    case 'Fuel Surcharge':
      return parseFloat(entry.fuelSurchargePercent) / 100;
    case 'Freight Rev (calculated)':
      return freightRev;
    case 'FS Charge (calculated)':
      return fuelSurcharge;
    case 'Terminal Arrival Time':
      return entry.terminalArrivalTime;
    case 'Terminal Depart Time':
      return entry.terminalDepartTime;
    case 'Terminal Total Time':
      return terminalTime;
    case 'Delivery Arrival time':
      return entry.deliveryArrivalTime;
    case 'Delivery Depart time':
      return entry.deliveryDepartTime;
    case 'Delivery total time':
      return deliveryTime;
    case 'Driver':
      return entry.driver;
    case 'Total DEM':
      return entry.totalDem;
    case 'Demurrage Charge (calculated)':
      return parseFloat(entry.totalDem || '0') * 100; // $100 per hour
    case 'Total Charges (calculated)':
      return freightRev + fuelSurcharge + (parseFloat(entry.totalDem || '0') * 100);
    case 'Notes':
      return entry.notes;
    default:
      console.log(`No mapping found for field: ${fieldName}`);
      return null;
  }
};

const getInvoiceFieldValue = (entry: InvoiceItem, fieldName: string | null): string | number | null => {
  if (!fieldName) return null;

  console.log(`Getting invoice field value for: ${fieldName}`, {
    entryValue: entry[fieldName as keyof InvoiceItem],
    allEntryData: entry
  });

  // Calculate derived values
  const freightRev = parseFloat((parseFloat(entry.gross || '0') * parseFloat(entry.rate || '0')).toFixed(2));
  const fuelSurcharge = parseFloat(((freightRev * (parseFloat(entry.fuelSurchargePercent || '0') / 100))).toFixed(2));

  // Direct field mappings
  let result: string | number | null = null;
  switch (fieldName) {
    // String fields
    case 'customer':
      result = entry.customer || '';
      break;
    case 'address':
      result = entry.address || '';
      break;
    case 'cityStateZip':
      result = entry.cityStateZip || '';
      break;
    case 'invoiceDate':
      result = entry.invoiceDate || '';
      break;
    case 'invoiceNumber':
      result = entry.invoiceNumber || '';
      break;
    case 'dueDate':
      result = entry.dueDate || '';
      break;
    case 'orderNumber':
      result = entry.orderNumber || '';
      break;
    case 'loadNumber':
      result = entry.loadNumber || '';
      break;
    case 'date':
      result = entry.date || '';
      break;
    case 'bolNumber':
      result = entry.bolNumber || '';
      break;
    case 'driver':
      result = entry.driver || '';
      break;
    case 'notes':
      result = entry.notes || '';
      break;

    // Numeric fields - ensure they are returned as numbers
    case 'miles':
      // Convert miles to whole number
      result = Math.round(parseFloat(entry.miles || '0'));
      break;
    case 'gross':
      result = parseFloat(entry.gross || '0');
      break;
    case 'rate':
      result = parseFloat(entry.rate || '0');
      break;
    case 'freightRev':
      result = freightRev;
      break;
    case 'fuelSurchargePercent':
      // Convert fuel surcharge percent to whole number
      result = Math.round(parseFloat(entry.fuelSurchargePercent || '0'));
      break;
    case 'fuelSurcharge':
      result = fuelSurcharge;
      break;
    case 'totalDem':
      result = parseFloat(entry.totalDem || '0');
      break;
    case 'demurrageCharge':
      result = parseFloat(entry.totalDem || '0') * 100; // $100 per hour
      break;
    case 'totalCharges':
      result = freightRev + fuelSurcharge + (parseFloat(entry.totalDem || '0') * 100);
      break;
    default:
      console.warn(`Unknown invoice field name: ${fieldName}`);
      return null;
  }

  console.log(`Field ${fieldName} result:`, result);
  return result;
};

// Format the date from YYYY-MM-DD to MM/DD/YY
const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '';
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year.slice(2)}`;
};

export const createExcelFromTemplate = async (brokerName: string, entries: InvoiceItem[]): Promise<Blob> => {
  try {
    if (!Array.isArray(entries) || entries.length === 0) {
      throw new Error('No entries provided');
    }

    console.log('Starting Excel generation with entries:', entries);

    // Load the template file - always use Loads.xlsx
    console.log('Loading template file: /templates/Loads.xlsx');
    const templateResponse = await fetch('/templates/Loads.xlsx');
    if (!templateResponse.ok) {
      throw new Error(`Failed to load template: ${templateResponse.statusText}`);
    }
    
    // Get the template as a blob and then as an array buffer
    const templateBlob = await templateResponse.blob();
    const templateBuffer = await templateBlob.arrayBuffer();
    console.log('Template file loaded successfully');

    // Create a new workbook
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(templateBuffer);
    console.log('Workbook created and template loaded');

    // Get the first worksheet
    const worksheet = workbook.getWorksheet(1);
    if (!worksheet) {
      throw new Error('Template worksheet not found');
    }
    console.log('Worksheet found');

    // Load the mapping file - always use Loads.txt
    console.log('Loading mapping file: Loads.txt');
    const mappings = await readMappingFile('Loads.txt');
    console.log('Mappings loaded:', mappings);

    // First, duplicate the template row for each entry
    const templateRow = 4;
    if (entries.length > 1) {
      // Store template row styles and formulas before duplicating
      const templateStyles = new Map<number, CellStyle>();
      const templateFormulas = new Map<number, string>();
      
      worksheet.getRow(templateRow).eachCell((cell, colNumber) => {
        templateStyles.set(colNumber, {
          font: cell.font,
          alignment: cell.alignment,
          border: cell.border,
          fill: cell.fill,
          numFmt: cell.numFmt
        });
        if (cell.formula) {
          templateFormulas.set(colNumber, cell.formula);
        }
      });

      for (let i = 1; i < entries.length; i++) {
        const targetRow = templateRow + i;
        
        // Copy the entire row
        const sourceRow = worksheet.getRow(templateRow);
        const targetRowObj = worksheet.getRow(targetRow);
        
        // Copy values and formatting
        sourceRow.eachCell((cell, colNumber) => {
          const targetCell = targetRowObj.getCell(colNumber);
          const style = templateStyles.get(colNumber);
          if (style) {
            Object.assign(targetCell, style);
          }
        });

        // Apply formulas with adjusted references
        templateFormulas.forEach((formula, colNumber) => {
          const adjustedFormula = formula.replace(/(\$?[A-Z]+)(\$?\d+)/g, (match, col, row) => {
            if (row.startsWith('$')) return match; // Keep absolute references
            return `${col}${parseInt(row) + i}`; // Adjust relative references
          });
          worksheet.getCell(targetRow, colNumber).value = { formula: adjustedFormula };
        });
      }
    }

    // Apply the mappings for each entry
    console.log('Applying mappings to worksheet...');
    entries.forEach((entry, entryIndex) => {
      const rowNumber = templateRow + entryIndex;
      
      for (const mapping of mappings) {
        try {
          // Replace the row number in the cell reference
          const column = mapping.cell.match(/^[A-Z]+/)?.[0];
          if (!column) {
            console.warn(`Invalid cell reference: ${mapping.cell}, skipping`);
            continue;
          }

          // Skip columns that contain formulas (M, N, AA, AB)
          if (['M', 'N', 'AA', 'AB'].includes(column)) {
            console.log(`Skipping column ${column} as it contains a formula`);
            continue;
          }
          
          const cellReference = `${column}${rowNumber}`;
          console.log(`Processing mapping for cell ${cellReference}:`, mapping);

          const cell = worksheet.getCell(cellReference);
          if (!cell) {
            console.warn(`Could not get cell ${cellReference}, skipping`);
            continue;
          }

          if (mapping.staticText) {
            // Set only the value, preserve formatting
            const existingStyle = {
              font: cell.font,
              alignment: cell.alignment,
              border: cell.border,
              fill: cell.fill,
              numFmt: cell.numFmt
            };
            cell.value = mapping.staticText;
            Object.assign(cell, existingStyle);
            console.log(`Set static text in cell ${cellReference}:`, mapping.staticText);
          } else if (mapping.fieldName) {
            const value = getFieldValue(entry, mapping.fieldName);
            if (value !== null) {
              // Only set the value if the cell doesn't have a formula
              if (!cell.formula) {
                // Store existing style
                const existingStyle = {
                  font: cell.font,
                  alignment: cell.alignment,
                  border: cell.border,
                  fill: cell.fill,
                  numFmt: cell.numFmt
                };

                // Special handling for Fuel Surcharge percentage
                if (column === 'L' || mapping.fieldName === 'Fuel Surcharge' || mapping.fieldName.toLowerCase().includes('surcharge')) {
                  // If the value is a decimal (e.g., 0.05), convert to percentage (5)
                  if (typeof value === 'number') {
                    const percentValue = value < 1 ? value * 100 : value;
                    cell.value = `${Math.round(percentValue)}%`;
                  } else {
                    // If it's already a string, make sure it has the % symbol
                    cell.value = value.toString().endsWith('%') ? value : `${value}%`;
                  }
                } else {
                  cell.value = value;
                }

                // Restore existing style
                Object.assign(cell, existingStyle);
                console.log(`Set field value in cell ${cellReference}:`, { field: mapping.fieldName, value: cell.value });
              } else {
                console.log(`Skipping cell ${cellReference} as it contains a formula:`, cell.formula);
              }
            } else {
              console.log(`No value found for field ${mapping.fieldName} in cell ${cellReference}`);
            }
          }
        } catch (error) {
          console.error(`Error processing cell for entry ${entryIndex + 1}:`, error);
          // Continue with other cells even if one fails
        }
      }
    });

    // Drop conditional-formatting rule types ExcelJS can't serialize, so Excel
    // doesn't flag the saved file as corrupt on open.
    sanitizeConditionalFormatting(worksheet);

    // Generate Excel file
    console.log('Generating Excel buffer...');
    const buffer = await workbook.xlsx.writeBuffer();
    console.log('Excel buffer generated successfully');
    return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

  } catch (error) {
    console.error('Error creating Excel file:', error);
    throw error;
  }
};

export const createInvoiceFromTemplate = async (brokerName: string, entry: InvoiceItem): Promise<Blob> => {
  try {
    validateEntry(entry);

    // Get broker information from Redux store
    const state = store.getState() as RootState;
    const broker = state.brokers.items.find(b => b.shortname === brokerName);
    
    if (!broker) {
      throw new Error(`Broker information not found for: ${brokerName}`);
    }

    // Load the template file based on invoiceSheet
    const templateFileName = entry.invoiceSheet === 'Fuel Masters' ? 'FM_Invoice.xlsx' : 'FW_Invoice.xlsx';
    console.log('Loading template file: /templates/' + templateFileName);
    const templateResponse = await fetch('/templates/' + templateFileName);
    if (!templateResponse.ok) {
      throw new Error(`Failed to load template: ${templateResponse.statusText} (${templateResponse.status})`);
    }
    
    // Get the template as a blob and then as an array buffer
    const templateBlob = await templateResponse.blob();
    const templateBuffer = await templateBlob.arrayBuffer();
    console.log('Template file loaded successfully, size:', templateBuffer.byteLength, 'bytes');

    // Create a new workbook
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(templateBuffer);
    console.log('Workbook created and template loaded');

    // List all worksheets
    const worksheetNames = workbook.worksheets.map(ws => ws.name);
    console.log('Available worksheets:', worksheetNames);

    // Try different methods to get the worksheet
    let worksheet = null;

    // Method 1: Try getting the first worksheet
    worksheet = workbook.getWorksheet(1);
    
    // Method 2: If that fails, try getting the worksheet named "Sheet1"
    if (!worksheet) {
      worksheet = workbook.getWorksheet('Sheet1');
    }

    // Method 3: If that fails, try getting the worksheet named "Invoice"
    if (!worksheet) {
      worksheet = workbook.getWorksheet('Invoice');
    }

    // Method 4: If that fails, try getting the first worksheet from the worksheets array
    if (!worksheet && workbook.worksheets.length > 0) {
      worksheet = workbook.worksheets[0];
    }

    // If we still don't have a worksheet, throw an error
    if (!worksheet) {
      throw new Error(`Template worksheet not found. Available worksheets: ${worksheetNames.join(', ')}`);
    }

    console.log('Using worksheet:', worksheet.name);

    // Load the mapping file - always use INVOICE.txt
    console.log('Loading mapping file: INVOICE.txt');
    const mappings = await readInvoiceMappingFile('INVOICE.txt');
    console.log('Mappings loaded:', mappings);

    // First, set the broker information in rows 15-18
    console.log('Setting broker information...');
    try {
      worksheet.getCell('A15').value = broker.name;
      worksheet.getCell('A16').value = broker.address;
      worksheet.getCell('A17').value = broker.phone;
      worksheet.getCell('A18').value = broker.email;
      console.log('Broker information set successfully');
    } catch (error) {
      console.error('Error setting broker information:', error);
      throw new Error('Failed to set broker information in template');
    }

    // Apply the rest of the mappings
    console.log('Applying field mappings...');
    for (const mapping of mappings) {
      try {
        // Skip broker information cells as we've already handled them
        if (['A15', 'A16', 'A17', 'A18'].includes(mapping.cell)) {
          continue;
        }

        const cell = worksheet.getCell(mapping.cell);
        if (!cell) {
          console.warn(`Could not get cell ${mapping.cell}, skipping`);
          continue;
        }

        if (mapping.staticText) {
          cell.value = mapping.staticText;
          console.log(`Set static text in cell ${mapping.cell}:`, mapping.staticText);
        } else if (mapping.fieldName) {
          const value = getInvoiceFieldValue(entry, mapping.fieldName);
          if (value !== null) {
            // Set the value and format based on type and cell
            if (typeof value === 'number') {
              cell.value = value;
              // Format specific cells as currency
              if (mapping.cell === 'I22' || mapping.cell === 'I23') {
                cell.numFmt = '$#,##0.00'; // Currency format for I22 and I23
              } else if (mapping.cell === 'H23') {
                // H23 is the fuel surcharge rate, which is a percentage (e.g. 54 -> "54%").
                // Use a literal-percent format so the value shows as-is with a "%" suffix;
                // a bare "%" format would multiply the number by 100.
                cell.numFmt = '0"%"';
              } else if (mapping.cell === 'D22' || mapping.cell === 'G22' || mapping.cell === 'H22') {
                // Keep General formatting for D22, G22, H22 (don't set numFmt)
                cell.numFmt = 'General';
              } else {
                cell.numFmt = '#,##0.00'; // Default decimal format for other numbers
              }
              console.log(`Set numeric value in cell ${mapping.cell}:`, { field: mapping.fieldName, value, format: cell.numFmt });
            } else {
              cell.value = value;
              console.log(`Set text value in cell ${mapping.cell}:`, { field: mapping.fieldName, value });
            }
          } else {
            console.log(`No value found for field ${mapping.fieldName} in cell ${mapping.cell}`);
          }
        }
      } catch (error) {
        console.error('Error processing cell:', error);
        // Continue with other cells even if one fails
      }
    }

    // Add the additional invoice line items (pump, demurrage, split, future items).
    //
    // The freight (row 22) and fuel surcharge (row 23) are fixed. Everything after
    // that is packed into consecutive rows starting at row 24, so there are never
    // gaps. Each row uses E:F (merged) = description, G = quantity, H = price/rate,
    // I = amount, and the amounts (I24..) roll into the SUM(I22:I34) subtotal
    // cached below.
    //
    // Most items have a quantity and per-unit rate (amount = qty * rate). The split
    // share is a flat amount with no rate (qty 1, rate cell left blank). To add a
    // future line item, push it onto `lineItems`.
    try {
      const ws = worksheet as ExcelJS.Worksheet;
      const LINE_ITEM_START_ROW = 24; // first row after freight (22) and fuel surcharge (23)
      const PUMP_RATE = 100; // $ per pump
      const DEMURRAGE_RATE = 100; // $ per demurrage hour

      const totalDem = parseFloat(entry.totalDem || '0') || 0;
      // Pump count is folded into Total Dem, so the demurrage quantity is the
      // remaining base/time demurrage (avoids double-counting the pumps).
      const pumpQty = entry.pumpEnabled ? parseFloat(entry.pumpCount || '0') || 0 : 0;
      const baseDemQty = totalDem - pumpQty;
      const splitShare = entry.splitShareAmount || 0;

      // Write a value into a line-item cell while forcing the alignment used by the
      // populated line items (row 22): description/qty/rate centered, amount
      // right-aligned. The empty template rows are missing some of this alignment.
      // We assign a freshly cloned style per cell rather than mutating properties
      // in place — ExcelJS shares one style object across the empty cells, so an
      // in-place `cell.numFmt = ...` leaks the format into siblings.
      const setCell = (
        addr: string,
        value: string | number,
        horizontal: 'center' | 'right',
        numFmt?: string
      ) => {
        const cell = ws.getCell(addr);
        cell.value = value;
        const style = JSON.parse(JSON.stringify(cell.style || {}));
        style.alignment = { ...(style.alignment || {}), horizontal };
        if (numFmt) style.numFmt = numFmt;
        cell.style = style;
      };

      // A line item has a label, quantity, amount, and an optional per-unit rate.
      // When the rate is omitted the rate cell (H) is left blank.
      type LineItem = { label: string; qty: number; amount: number; rate?: number };
      const writeLineItem = (row: number, item: LineItem) => {
        setCell(`E${row}`, item.label, 'center');
        setCell(`G${row}`, item.qty, 'center', 'General');
        if (item.rate !== undefined) {
          setCell(`H${row}`, item.rate, 'center', '$#,##0.00');
        }
        setCell(`I${row}`, item.amount, 'right', '$#,##0.00');
      };

      // Display order of the additional line items; each renders only when present.
      const lineItems: LineItem[] = [];
      if (pumpQty > 0) {
        lineItems.push({ label: 'Pump', qty: pumpQty, rate: PUMP_RATE, amount: pumpQty * PUMP_RATE });
      }
      if (baseDemQty > 0) {
        lineItems.push({
          label: 'Demurrage',
          qty: baseDemQty,
          rate: DEMURRAGE_RATE,
          amount: baseDemQty * DEMURRAGE_RATE,
        });
      }
      if (splitShare > 0) {
        // Flat split share: quantity 1, no rate, amount = the member's share.
        lineItems.push({ label: 'Split', qty: 1, amount: splitShare });
      }

      let row = LINE_ITEM_START_ROW;
      for (const item of lineItems) {
        writeLineItem(row, item);
        console.log(`Added ${item.label} line item on row ${row}:`, {
          qty: item.qty,
          amount: item.amount,
        });
        row++;
      }
    } catch (error) {
      console.error('Error writing additional line items:', error);
    }

    // The Subtotal (I35) and Grand Total (I38) cells contain SUM(I22:I34) formulas.
    // ExcelJS does not evaluate formulas, so cache the computed result on each
    // formula cell. This keeps the formula (Excel still recalculates on open) while
    // letting non-recalculating consumers — like the PDF renderer — show the total.
    try {
      let subtotal = 0;
      for (let row = 22; row <= 34; row++) {
        const value = worksheet.getCell(`I${row}`).value;
        if (typeof value === 'number') {
          subtotal += value;
        }
      }
      for (const totalCell of ['I35', 'I38']) {
        const cell = worksheet.getCell(totalCell);
        const current = cell.value as any;
        if (current && typeof current === 'object' && 'formula' in current) {
          cell.value = { formula: current.formula, result: subtotal };
        } else {
          cell.value = subtotal;
        }
      }
      console.log('Computed invoice total:', subtotal);
    } catch (error) {
      console.error('Error computing invoice total:', error);
    }

    // Drop conditional-formatting rule types ExcelJS can't serialize, so Excel
    // doesn't flag the saved file as corrupt on open.
    sanitizeConditionalFormatting(worksheet);

    // Generate Excel file
    console.log('Generating Excel buffer...');
    const buffer = await workbook.xlsx.writeBuffer();
    console.log('Excel buffer generated successfully, size:', buffer.byteLength, 'bytes');
    return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

  } catch (error) {
    console.error('Error creating invoice:', error);
    throw error;
  }
}; 